// MeetingList.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import MeetingDetail from "./MeetingDetail";
import "./MeetingList.scss";

const MS_MEETINGS_API =
  "https://meetinghub.insync-ai-suite.com/team/api/meetings";
const GOOGLE_MEETINGS_API =
  "https://meetinghub.insync-ai-suite.com/api/v1/meetings";

// ── tiny inline SVG helpers ──────────────────────────────────────────────────
const Icon = {
  Calendar: () => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  FileText: () => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  Clipboard: () => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    </svg>
  ),
  Refresh: () => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  ),
  Inbox: () => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  ),
};

// ── meeting state helper ─────────────────────────────────────────────────────
function getMeetingState(meeting) {
  const now = new Date();
  const start = new Date(meeting.startTime);
  const end = new Date(meeting.endTime);
  const transcriptMeta = meeting.meetingTranscript?.transcriptMeta;

  // 🥇 Real meeting signals (best)
  if (transcriptMeta?.endTime) return "FINISHED";
  if (transcriptMeta?.startTime) return "ONGOING";

  // 🥈 Scheduled fallback
  if (now < start) return "NOT_STARTED";
  if (now >= start && now <= end) return "ONGOING";
  return "FINISHED";
}

// ── date formatter ───────────────────────────────────────────────────────────
function formatDateTime(iso) {
  if (!iso) return { date: "—", time: "" };
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }),
  };
}

// ── state badge ──────────────────────────────────────────────────────────────
function MeetingStateBadge({ state }) {
  const config = {
    FINISHED: { label: "Finished", className: "state-badge state-finished" },
    ONGOING: { label: "Ongoing", className: "state-badge state-ongoing" },
    NOT_STARTED: {
      label: "Not Started",
      className: "state-badge state-not-started",
    },
  };
  const { label, className } = config[state] || config.NOT_STARTED;
  return <span className={className}>{label}</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
function MeetingList() {
  const [meetings, setMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [activeTab, setActiveTab] = useState("all");
  // ── NEW: completion filter state ──
  const [completionFilter, setCompletionFilter] = useState("all"); // "all" | "finished" | "unfinished"
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(
      () => setNotification({ show: false, message: "", type: "" }),
      3000,
    );
  };

  const fetchMeetings = async () => {
    setLoading(true);
    try {
      const [msRes, googleRes] = await Promise.all([
        axios.get(MS_MEETINGS_API),
        axios.get(GOOGLE_MEETINGS_API),
      ]);

      const msMeetings = (msRes.data.data || []).map((m) => ({
        id: m.eventId,
        _id: m.eventId,
        eventId: m.eventId,
        subject: m.subject,
        startTime: m.start?.dateTime,
        endTime: m.end?.dateTime,
        organizerEmail: m.organizer?.address,
        organizerName: m.organizer?.name,
        provider: "MICROSOFT",
        joinUrl: null,
        hasTranscript: false,
        hasMom: false,
        meetingTranscript: null,
      }));

      const googleMeetings = (googleRes.data.items || []).map((m) => ({
        id: m._id,
        _id: m._id,
        eventId: m.providerEventId,
        subject: m.subject,
        startTime: m.startTime,
        endTime: m.endTime,
        organizerEmail: m.organizerEmail,
        organizerName: m.organizerEmail?.split("@")[0],
        provider: "GOOGLE",
        joinUrl: m.joinUrl,
        hasTranscript: !!m.meetingTranscript?.mergedText,
        hasMom: !!m.meetingMom?.summary,
        transcript: m.meetingTranscript,
        mom: m.meetingMom,
        meetingTranscript: m.meetingTranscript,
      }));

      const all = [...msMeetings, ...googleMeetings].sort(
        (a, b) => new Date(b.startTime) - new Date(a.startTime),
      );
      setMeetings(all);
    } catch (err) {
      console.error(err);
      showNotification("Failed to fetch meetings", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, []);

  const handleViewDetails = (meeting) => {
    setSelectedMeeting(meeting);
    setView("detail");
  };
  const handleBackToList = () => {
    setView("list");
    setSelectedMeeting(null);
  };

  // ── filtering: provider tab + completion filter ──────────────────────────
  const filteredMeetings = meetings.filter((m) => {
    // provider tab
    if (activeTab === "microsoft" && m.provider !== "MICROSOFT") return false;
    if (activeTab === "google" && m.provider !== "GOOGLE") return false;

    // completion filter
    if (completionFilter !== "all") {
      const state = getMeetingState(m);
      if (completionFilter === "finished" && state !== "FINISHED") return false;
      if (completionFilter === "unfinished" && state === "FINISHED")
        return false;
    }

    return true;
  });

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading)
    return (
      <div className="meeting-list-container">
        <div className="loading-screen">
          <div className="spinner" />
          <p>Loading meetings…</p>
        </div>
      </div>
    );

  // ── Detail view ──────────────────────────────────────────────────────────
  if (view === "detail" && selectedMeeting)
    return (
      <MeetingDetail
        meeting={selectedMeeting}
        onBack={handleBackToList}
        onRefresh={fetchMeetings}
        showNotification={showNotification}
      />
    );

  // ── List view ────────────────────────────────────────────────────────────
  return (
    <div className="meeting-list-container">
      {notification.show && (
        <div className={`notification notification-${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* ── Header ── */}
      <div className="list-header">
        <div className="header-content">
          <div className="header-top">
            <h1 className="page-header-title">Meetings</h1>
            <button className="header-refresh-btn" onClick={fetchMeetings}>
              <Icon.Refresh /> Refresh
            </button>
          </div>
        </div>

        <div className="stats-strip">
          <div className="stat-item">
            <div className="stat-icon">
              <Icon.Calendar />
            </div>
            <div className="stat-info">
              <div className="stat-number">{meetings.length}</div>
              <div className="stat-label">Total Meetings</div>
            </div>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <div className="stat-icon">
              <Icon.FileText />
            </div>
            <div className="stat-info">
              <div className="stat-number">
                {meetings.filter((m) => m.hasTranscript).length}
              </div>
              <div className="stat-label">With Transcripts</div>
            </div>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <div className="stat-icon">
              <Icon.Clipboard />
            </div>
            <div className="stat-info">
              <div className="stat-number">
                {meetings.filter((m) => m.hasMom).length}
              </div>
              <div className="stat-label">With MOM</div>
            </div>
          </div>
          <div className="stat-divider" />
          {/* ── NEW stat: Finished count ── */}
          <div className="stat-item">
            <div className="stat-icon">
              <Icon.Calendar />
            </div>
            <div className="stat-info">
              <div className="stat-number">
                {
                  meetings.filter((m) => getMeetingState(m) === "FINISHED")
                    .length
                }
              </div>
              <div className="stat-label">Finished</div>
            </div>
          </div>
          <div className="stat-divider" />
          {/* ── NEW stat: Unfinished count ── */}
          <div className="stat-item">
            <div className="stat-icon">
              <Icon.Calendar />
            </div>
            <div className="stat-info">
              <div className="stat-number">
                {
                  meetings.filter((m) => getMeetingState(m) !== "FINISHED")
                    .length
                }
              </div>
              <div className="stat-label">Unfinished</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabs + Completion Filter row ── */}
      <div className="tabs-section">
        <div className="tabs">
          {[
            { key: "all", label: "All Meetings" },
            { key: "microsoft", label: "Microsoft Teams" },
            { key: "google", label: "Google Meet" },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`tab ${activeTab === key ? "active" : ""}`}
              onClick={() => setActiveTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── NEW: completion filter select ── */}
        <div className="completion-filter-wrap">
          <select
            className="completion-filter-select"
            value={completionFilter}
            onChange={(e) => setCompletionFilter(e.target.value)}
          >
            <option value="all">All States</option>
            <option value="finished">Finished</option>
            <option value="unfinished">Unfinished</option>
          </select>
        </div>

        <div className="meeting-count-badge">
          {filteredMeetings.length}{" "}
          {filteredMeetings.length === 1 ? "Meeting" : "Meetings"}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="meetings-table-wrapper">
        {filteredMeetings.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <Icon.Inbox />
            </div>
            <h3>No meetings found</h3>
            <p>Try a different tab or filter.</p>
          </div>
        ) : (
          <table className="meetings-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Organizer</th>
                <th>Start</th>
                <th>End</th>
                <th>Provider</th>
                <th>State</th> {/* ── NEW column ── */}
                <th>Resources</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredMeetings.map((meeting) => {
                const start = formatDateTime(meeting.startTime);
                const end = formatDateTime(meeting.endTime);
                const state = getMeetingState(meeting); // ── NEW
                return (
                  <tr key={meeting.id}>
                    <td className="subject-cell">
                      <div className="subject-text">
                        {meeting.subject || "Untitled Meeting"}
                      </div>
                    </td>
                    <td className="organizer-cell">
                      {meeting.organizerName && (
                        <span className="organizer-name">
                          {meeting.organizerName}
                        </span>
                      )}
                      <span className="organizer-email">
                        {meeting.organizerEmail}
                      </span>
                    </td>
                    <td className="time-cell">
                      <div className="date-part">{start.date}</div>
                      <div className="time-part">{start.time}</div>
                    </td>
                    <td className="time-cell">
                      <div className="date-part">{end.date}</div>
                      <div className="time-part">{end.time}</div>
                    </td>
                    <td>
                      <span
                        className={`provider-badge provider-${meeting.provider.toLowerCase()}`}
                      >
                        {meeting.provider === "MICROSOFT" ? "Teams" : "Google"}
                      </span>
                    </td>

                    {/* ── NEW: state cell ── */}
                    <td>
                      <MeetingStateBadge state={state} />
                    </td>
                    <td>
                      <div className="resources-badges">
                        {meeting.hasTranscript && (
                          <span className="resource-badge transcript-badge">
                            Transcript
                          </span>
                        )}
                        {meeting.hasMom && (
                          <span className="resource-badge mom-badge">MOM</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <button
                        className="view-details-btn"
                        onClick={() => handleViewDetails(meeting)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default MeetingList;
